// Natural-language frame revision: turn a one-line note ("deepen the barrel,
// warmer sky, more electric teal on the wave face") into a NEW, non-destructive
// VERSION of a graded still — baked server-side and auto-grouped with the
// original under the same source frame (so it shows up in Compare).
//
// It reuses the exact pieces the editor's color assistant already uses:
//   • /ai/color — Grok SEES the frame + the current grade and returns slider ops
//     (absolute values) plus optional local masks (sky / barrel / subject).
//   • applyGradeOps + mask append — merge that proposal onto the current grade.
//   • createStillGrabAsset — re-grab the source frame and bake the new grade as a
//     sibling variation (grouped by source clip + frame index).
//
// Entry point from the photo card and the lightbox, so a revision never requires
// opening the full editor — but the same grade math is shared, so an edit made
// here and one made in the editor are identical.

import firebase from '../firebaseConfig';
import {
  PRESETS,
  applyLook,
  blendGrade,
  stampPreset,
  applyGradeOps,
  activeAdjustments,
  ADJUSTMENT_SCHEMA,
  DEFAULT_GRADE,
} from './grade';
import { framingToCrop } from './crop';
import { createStillGrabAsset } from '../data/firestore';

const API = process.env.REACT_APP_UPLOAD_API || '';

// Best public image for the asset, in resolution order (the model only needs a
// ~1280px read, so the magazine derivative is plenty and cheap to fetch).
function bestImageUrl(a) {
  return (
    a?.derivatives?.magazine
    || a?.displayUrl
    || a?.derivatives?.fulljpeg
    || a?.thumbUrl
    || ''
  );
}

// Load a cross-origin image and re-encode it as a downscaled JPEG data URL so it
// can ride inside the /ai/color JSON body. Returns { dataUrl, width, height } so
// the caller can also work out the displayed aspect ratio for reframing. dataUrl
// is null if the image can't be read (tainted canvas / network) — the assistant
// still works from the grade context, just without seeing the pixels.
function loadFrameImage(url, maxW = 1280) {
  return new Promise((resolve) => {
    if (!url) return resolve({ dataUrl: null, width: 0, height: 0 });
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const nw = img.naturalWidth || maxW;
      const nh = img.naturalHeight || maxW;
      try {
        const scale = Math.min(1, maxW / nw);
        const w = Math.max(1, Math.round(nw * scale));
        const h = Math.max(1, Math.round(nh * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.86), width: nw, height: nh });
      } catch {
        resolve({ dataUrl: null, width: nw, height: nh });
      }
    };
    img.onerror = () => resolve({ dataUrl: null, width: 0, height: 0 });
    // Cache-bust so a just-re-baked frame isn't read from a stale CDN copy.
    img.src = url + (url.includes('?') ? '&' : '?') + 'nlv=' + Date.now();
  });
}

// Merge an /ai/color proposal (preset + ops + masks) onto a grade. Mirrors
// applyProposal in ColorAssistant.js so card/lightbox revisions match the editor.
function applyProposal(grade, ops, preset, masks, clearMasks) {
  let next = grade;
  if (preset && preset.id) {
    const p = PRESETS.find((x) => x.id === preset.id);
    if (p) {
      const look = applyLook(next, p.grade);
      const amt = (preset.amount == null ? 100 : preset.amount) / 100;
      next = blendGrade(next, look, amt);
      next = stampPreset(next, p, preset.amount == null ? 100 : preset.amount);
    }
  }
  next = applyGradeOps(next, ops || []);
  if (clearMasks || (Array.isArray(masks) && masks.length)) {
    const existing = clearMasks ? [] : (next.masks || []);
    next = { ...next, masks: [...existing, ...(masks || [])] };
  }
  return next;
}

// A still can only be re-graded into a new version if we know the SOURCE frame
// to re-grab from (source clip + frame). Uploaded photos that aren't frame grabs
// can't (yet) be versioned this way.
export function canReviseAsset(asset) {
  return !!(asset && (asset.lineage?.sourceAssetId || asset.stillGrab?.sourceAssetId));
}

// Short, filesystem-friendly tail for the version name, from the user's note.
function shortNote(prompt) {
  return String(prompt || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 48);
}

// Run one natural-language revision and queue the new version's bake.
// Returns { newId, reply, ops, masks }.
export async function reviseFrameToVersion(asset, prompt) {
  const note = String(prompt || '').trim();
  if (!note) throw new Error('Describe the change you want first.');
  if (!API) throw new Error('AI assistant isn’t configured in this build.');
  if (!canReviseAsset(asset)) {
    throw new Error('This photo isn’t a frame grab, so it can’t be re-versioned automatically.');
  }

  const baseGrade = asset.grade || DEFAULT_GRADE;
  const activeMap = Object.fromEntries(activeAdjustments(baseGrade).map((a) => [a.key, a.value]));
  const schema = ADJUSTMENT_SCHEMA.map((s) => ({
    key: s.key, label: s.label, min: s.min, max: s.max, step: s.step, unit: s.unit,
    default: activeMap[s.key] == null ? s.default : activeMap[s.key],
  }));

  const frame = await loadFrameImage(bestImageUrl(asset));
  const images = frame.dataUrl ? [{ dataUrl: frame.dataUrl, label: 'current graded frame' }] : [];
  const displayedAR = frame.width && frame.height ? frame.width / frame.height : null;

  const user = firebase.auth().currentUser;
  const token = user ? await user.getIdToken() : null;

  const res = await fetch(`${API}/ai/color`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      message: note,
      schema,
      activeAdjustments: activeAdjustments(baseGrade).map((a) => ({ key: a.key, value: a.value })),
      presets: PRESETS.filter((p) => p.id !== 'none').map((p) => ({ id: p.id, name: p.name })),
      currentPreset: baseGrade._preset ? { id: baseGrade._preset.id, amount: baseGrade._preset.amount } : null,
      images,
    }),
  });
  if (!res.ok) {
    let detail = '';
    try { const b = await res.json(); detail = b?.error || ''; } catch { /* noop */ }
    throw new Error(detail || `AI request failed (${res.status})`);
  }
  const data = await res.json();
  const ops = Array.isArray(data.ops) ? data.ops : [];
  const preset = data.preset || null;
  const masks = Array.isArray(data.masks) ? data.masks : [];
  const clearMasks = data.clearMasks === true;
  const framing = data.framing && data.framing.found ? data.framing : null;
  const upscale = data.upscale && data.upscale.enabled ? data.upscale : null;

  if (!ops.length && !preset && !masks.length && !clearMasks && !framing && !upscale) {
    const why = data.reply ? ` (${data.reply})` : '';
    throw new Error(`The assistant didn’t propose a change${why}.`);
  }

  const newGrade = applyProposal(baseGrade, ops, preset, masks, clearMasks);
  const redSource = !!(asset.lineage?.sourceIsRaw || asset.isRaw);

  // Reframe / zoom, if the model proposed one. The crop composes over any crop
  // the current version already has; zoom is clamped to a safe max for the source
  // so we never punch a low-res frame into mush.
  let cropOut = asset.crop || null;
  let framingNote = '';
  if (framing && displayedAR) {
    const fr = framingToCrop(framing, { displayedAR, existingCrop: asset.crop || null, redSource });
    if (fr.crop) {
      cropOut = fr.crop;
      framingNote = `Reframed at ${fr.zoom.toFixed(1)}×${fr.clamped ? ' (clamped from a higher zoom to keep detail sharp)' : ''}.`;
    }
  }

  const source = {
    id: asset.lineage?.sourceAssetId || asset.stillGrab?.sourceAssetId || '',
    name: asset.lineage?.sourceName || asset.name || 'clip',
  };
  const frameIndex = Number.isFinite(Number(asset.stillGrab?.frameIndex))
    ? Number(asset.stillGrab.frameIndex) : null;
  const timeSec = asset.stillGrab?.timeSec ?? asset.lineage?.sourceTimeSec ?? 0;

  const baseName = String(asset.name || source.name || 'frame').replace(/\.[^.]+$/, '');
  const name = `${baseName} · “${shortNote(note)}”`;

  const upscaleNote = upscale
    ? `Upscaling ${upscale.scale || 2}× with ${upscale.model || 'swinir'} before the grade.`
    : '';

  const newId = await createStillGrabAsset(asset.tripId, {
    source,
    timeSec,
    frameIndex,
    grade: newGrade,
    applyGrade: true,
    crop: cropOut,
    redSource,
    name,
    tags: ['nl-revise'],
    // Carry the prompt + the parent variation so the edit tree and any future
    // "learn from accepted edits" pass can recover the intent.
    revisedFromAssetId: asset.id || '',
    nlPrompt: note,
    // Reframe → upscale → grade: the worker applies the crop + AI upscale to a
    // working negative before metering/grading so detail is recovered up front.
    upscale: upscale || null,
  });

  const reply = [data.reply || 'New version queued.', framingNote, upscaleNote].filter(Boolean).join(' ');
  return { newId, reply, ops, masks, framing: framing || null, upscale: upscale || null };
}
